import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  MultiSelect,
  PriceBandIcon,
  Prompt,
  SearchField,
  SnackbarStatus,
  Spinner,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import EmptyState from 'src/components/EmptyState';
import SkeletonContainer from 'src/components/SkeletonContainer';
import Burger from 'src/assets/images/Burger';
import { StyledEmptyStateContainer } from './Items.styles';
import { deleteMenuItem } from 'src/api/deleteMenuItem/deleteMenuItem';
import styles from './Items.module.scss';
import { CreateGlobalItemWizard } from '../../components/CreateGlobalItemWizard/CreateGlobalItemWizard';
import { ItemType } from 'src/constants';
import { PageHeadingWithSync } from '../../components/PageHeadingWithSync/PageHeadingWithSync';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { ProductLibraryTable, ProductTableRow } from 'src/components/ProductLibraryTable';
import useProductLibraryItems from 'src/hooks/useProductLibraryItems/useProductLibraryItems';
import {
  IProductLibraryStatus,
  useProductLibraryStatus,
} from '../../hooks/useProductLibraryStatus/useProductLibraryStatus';
import { EProductLibraryStatus } from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { useRejectSync } from 'src/hooks/useRejectSync/useRejectSync';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { getSyncEndpoint } from '../../helpers/getSyncEndpoint/getSyncEndpoint';
import { SpinnerContainer } from 'src/components/SpinnerContainer';

interface INoItemsProps {
  handleAddNewMenuItemClick: () => void;
  searchQuery: string;
  hasActiveFilters: boolean;
}

const NoItems = ({ handleAddNewMenuItemClick, searchQuery, hasActiveFilters }: INoItemsProps) => {
  if (searchQuery) {
    return (
      <div className={styles['search-result']}>
        <Typography size="large" inline>
          No results found for{' '}
        </Typography>
        <Typography size="large" weight="semi-bold" inline>
          &quot;{searchQuery}&quot;
        </Typography>
      </div>
    );
  }

  if (hasActiveFilters) {
    return (
      <div className={styles['search-result']}>
        <Typography size="large">No results found for the selected filters</Typography>
      </div>
    );
  }

  return (
    <StyledEmptyStateContainer>
      <EmptyState
        emptySectionText="Add your first product"
        buttonText="New product"
        Icon={Burger}
        onClick={handleAddNewMenuItemClick}
      />
    </StyledEmptyStateContainer>
  );
};

export const Items = (): ReactElement => {
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchType, setSearchType] = useState<ItemType[]>([]);
  const [searchRanges, setSearchRanges] = useState<string[]>([]);
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(false);
  const [menuItemToDelete, setMenuItemToDelete] = useState<ProductTableRow | null>(null);
  const [isItemWizardOpen, setIsItemWizardOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();

  const { refetch: refetchExternalSystems, data: externalSystems } = useExternalSystems();

  const debouncedSearchQuery = useDebounce(searchQuery);
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading: areProductsLoading,
  } = useProductLibraryItems({
    searchQuery: debouncedSearchQuery,
    type: searchType.length > 0 ? searchType : null,
    ranges: searchRanges.length > 0 ? searchRanges : null,
  });
  const { rejectSync } = useRejectSync();

  const onSyncStatusUpdate = (data: IProductLibraryStatus) => {
    // only refetch products after status changes from PENDING -> SUCCESS sync
    // status call count avoids refetch on page render if a previous sync was successful
    if (data?.sync_status === EProductLibraryStatus.SUCCESS && data.callCount > 1) {
      queryClient.refetchQueries({ queryKey: [QueryKeys.productSizes] });
      refetchExternalSystems();
    }
  };
  const isProductSyncEnabled = useMemo(() => !!getSyncEndpoint(externalSystems), [externalSystems]);
  const { isSyncInProgress, triggerSync, syncInfo } = useProductLibraryStatus({
    onSyncStatusUpdate,
    enabled: isProductSyncEnabled,
  });

  const { data: ranges, isLoading: isRangesLoading } = useRanges();
  const rangeOptions =
    ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) || [];

  const typeOptions = [ItemType.STANDARD, ItemType.MODIFIER, ItemType.BUNDLE, ItemType.NON_FOOD].map((type, index) => ({
    id: type,
    label: `${type[0].toUpperCase()}${type.slice(1).toLowerCase()}`,
    display_order: index,
  }));

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handlePromptToggle = useCallback(() => setIsPromptVisible((prevState) => !prevState), [setIsPromptVisible]);

  const handleOpenDeletePrompt = useCallback(
    (menuItem: ProductTableRow) => {
      setMenuItemToDelete(menuItem);
      handlePromptToggle();
    },
    [setMenuItemToDelete, handlePromptToggle],
  );

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteMenuItem(menuItemToDelete?.item_id);
      trackEvent('product_deleted', { productId: menuItemToDelete?.item_id, productName: menuItemToDelete?.name });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully removed "${menuItemToDelete?.name}" from the item library.`,
      });

      await refetch();
    } catch (error) {
      captureException(error, { tags: { action: 'delete_product', productId: menuItemToDelete?.item_id } });
      error?.message?.includes('You cannot delete')
        ? showSnackbar({
            status: SnackbarStatus.ERROR,
            message: `Unable to delete ${menuItemToDelete?.name}`,
          })
        : showSnackbar({
            status: SnackbarStatus.ERROR,
            message: error?.message,
          });
    } finally {
      setIsDeleting(false);
      handlePromptToggle();
    }
  };

  const menuItems = useMemo(() => {
    return data?.pages.flatMap((page) => page.data);
  }, [data]);

  useEffect(() => {
    document.title = 'Items';
    trackEvent('page_viewed', { page: 'product_library' });
  }, []);

  return (
    <div className={styles['page-container']}>
      <PageHeadingWithSync
        isSyncInProgress={isSyncInProgress}
        triggerSync={triggerSync}
        isSyncEnabled={true}
        onPlusClick={() => setIsItemWizardOpen(true)}
        syncInfo={syncInfo}
        rejectSync={rejectSync}
      />

      {isRangesLoading ? (
        <SpinnerContainer>
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
      ) : (
        <>
          <div className={styles['search-container']}>
            <SearchField isDisabled={false} placeholder="Search" onChange={handleOnChange} />
            {ranges?.length > 0 && (
              <MultiSelect
                Icon={PriceBandIcon}
                label="Ranges"
                options={rangeOptions}
                selected={searchRanges}
                onChange={setSearchRanges}
                disabled={ranges?.length === 0}
              />
            )}
            {typeOptions.length > 0 && (
              <MultiSelect
                Icon={PriceBandIcon}
                label="Types"
                options={typeOptions}
                selected={searchType}
                onChange={(ids: string[]) => setSearchType(ids as ItemType[])}
              />
            )}
          </div>

          <CreateGlobalItemWizard open={isItemWizardOpen} closeModal={() => setIsItemWizardOpen(false)} />

          {areProductsLoading || isSyncInProgress ? (
            <SkeletonContainer hiddenTitle />
          ) : (
            <>
              {menuItems && menuItems.length > 0 ? (
                <main className={styles['table-container']}>
                  {debouncedSearchQuery && (
                    <div className={styles['search-result']}>
                      <Typography size="large" inline>
                        Search results for{' '}
                      </Typography>
                      <Typography size="large" weight="semi-bold" inline>
                        &quot;{debouncedSearchQuery}&quot;
                      </Typography>
                    </div>
                  )}
                  <Prompt
                    promptHeading={`Are you sure you want to delete "${menuItemToDelete?.name}"?`}
                    closePrompt={handlePromptToggle}
                    open={isPromptVisible}
                    buttonHandler={handleDelete}
                    buttonText="Delete"
                    backButton={true}
                    size="small"
                    isSubmitting={isDeleting}
                  >
                    You will remove this product from the product library and all menus it appears within.
                  </Prompt>
                  <ProductLibraryTable
                    data={menuItems}
                    onDelete={handleOpenDeletePrompt}
                    fetchNextPage={fetchNextPage}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                  />
                </main>
              ) : (
                <NoItems
                  handleAddNewMenuItemClick={() => setIsItemWizardOpen(true)}
                  searchQuery={searchQuery}
                  hasActiveFilters={searchType.length > 0 || searchRanges.length > 0}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
